import { faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiListSensors } from "apis/SensorApi";
import { apiGetUserDeviceGroups } from "apis/UserDeviceGroups";
import FanLoadingIcon from "components/FanLoadingIcon";
import Icon, { IconList } from "components/Icon";
import Pagination from "components/Pagination";
import { CURRENT_PAGE_DEFAULT, DEFAULT_SPLIT, SPLIT_OPTIONS } from "constants/config";
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE, TEXT_SPLIT_DISPLAY } from "constants/message";
import { useAuth } from "context/AuthContext";
import { useDialog } from "context/DialogContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./SplitPage.css";
import CameraList from "./components/CameraList";
import SelectBox from "./components/SelectBox";

const patternNumbers = [1, 2, 3, 4, 5];

function useDeviceSize() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return size;
}

const SplitPage = () => {
  const { currentUser } = useAuth();
  const [splitOption, setSplitOption] = useState(DEFAULT_SPLIT);
  const [selectedGroup, setSelectedGroup] = useState(1);
  const [sensors, setSensors] = useState([]);
  const { error } = useDialog();
  const [isLoading, setIsLoading] = useState(true);
  const [groupOptions, setGroupOptions] = useState([]);
  const [manageableSensorIds, setManageableSensorIds] = useState([]);
  const [existingGroups, setExistingGroups] = useState(null);

  const [curPage, setCurPage] = useState(CURRENT_PAGE_DEFAULT);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { width, height } = useDeviceSize();

  useEffect(() => {
    const fetchDataAndProcess = async () => {
      if (!currentUser || !currentUser.id) {
        setIsLoading(false);
        setGroupOptions([]);
        setExistingGroups(null);
        return;
      }
      setIsLoading(true);
      try {
        const sensorsResult = await apiListSensors();
        const sensorsData = sensorsResult.success ? sensorsResult.data.items : [];
        setSensors(sensorsData);

        setManageableSensorIds(currentUser.data?.nameList || []);
        const groupsResult = await apiGetUserDeviceGroups(currentUser.id);
        const groupsData = groupsResult.success && groupsResult.data ? groupsResult.data : null;
        setExistingGroups(groupsData);
        const generatedOptions = [];
        if (groupsData) {
          patternNumbers.forEach((num) => {
            const groupKey = `group${num}`;
            const groupName = groupsData[groupKey]?.name || `Group ${num}`;
            generatedOptions.push({ label: groupName, value: num });
          });
        }
        setGroupOptions(generatedOptions);
      } catch (err) {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDataAndProcess();
  }, [currentUser, error]);

  useEffect(() => {
    const handler = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setIsFullscreen(isFull);
    };
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);


  const calculateContainerWidth = useCallback(() => {
    if (containerRef.current) {
      if (isFullscreen) {
        return height / 9 * 16;
      } else {
        if (width < height) return width;
        return (height - 53) / 9 * 16;
      }
    }
  }, [isFullscreen, height, width]);


  const enterFullscreen = () => {
    if (containerRef.current.requestFullscreen) {
      containerRef.current.requestFullscreen();
    } else if (containerRef.current.webkitRequestFullscreen) {
      containerRef.current.webkitRequestFullscreen(); // Safari
    } else if (containerRef.current.msRequestFullscreen) {
      containerRef.current.msRequestFullscreen(); // IE/Edge
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  };

  const cameraItems = useMemo(() => {
    if (!existingGroups || !sensors || sensors.length === 0) {
      return [];
    }
    const items = [];
    const groupKey = `group${selectedGroup}`;
    const devicesInSelectedGroup = existingGroups[groupKey]?.devices || [];
    devicesInSelectedGroup.forEach((device) => {
      const sensor = sensors.find((s) => s.id === device.id && manageableSensorIds.includes(s.id));
      if (sensor) {
        const cameraDir1 = sensor.data.camera1Direction;
        device.directions.forEach((dir) => {
          const cameraNumber = dir === cameraDir1 ? 1 : 2;
          items.push({
            ...sensor,
            cameraNumber: cameraNumber,
          });
        });
      }
    });
    return items.sort((a, b) => {
      const idA = a.managementId ? (isNaN(Number(a.managementId)) ? a.managementId : Number(a.managementId)) : "";
      const idB = b.managementId ? (isNaN(Number(b.managementId)) ? b.managementId : Number(b.managementId)) : "";
      if (idA !== idB) {
        return idA > idB ? 1 : idA < idB ? -1 : 0;
      }
      const hasSwap = a.data?.swapImageCamera || false;
      return hasSwap ? b.cameraNumber - a.cameraNumber : a.cameraNumber - b.cameraNumber;
    });
  }, [selectedGroup, existingGroups, sensors, manageableSensorIds]);

  return (
    <>
      <div className="split-page-container">
        <div className="split-page-flex-row split-page-header">
          <div className="split-page-title">
            <Icon icon={IconList.iconListing} iconStyle="split-page-header-icon" />
            <label className="split-page-header-text">{TEXT_SPLIT_DISPLAY}</label>
          </div>
          <div className="split-page-control">
            <SelectBox
              className="split-page-sensor-status-select"
              value={selectedGroup}
              onChange={(e) => {
                setSelectedGroup(Number(e.target.value));
                setCurPage(CURRENT_PAGE_DEFAULT);
              }}
              options={groupOptions}
            />
            <SelectBox
              className="split-page-sensor-status-select"
              value={splitOption}
              onChange={(e) => {
                setSplitOption(Number(e.target.value));
                setCurPage(CURRENT_PAGE_DEFAULT);
              }}
              options={SPLIT_OPTIONS}
            />
            <button className="split-page-sensor-icon-button" onClick={enterFullscreen}>
              <FontAwesomeIcon icon={faExpand} className="split-page-sensor-icon-button-icon" />
            </button>
          </div>
          <div className="split-page-pagination split-page-pagination-large-screen">
            <Pagination pageNum={Math.ceil(cameraItems.length / splitOption)} curPage={curPage} onPageChange={(page) => setCurPage(page)} itemNumPerPage={splitOption} total={cameraItems.length} isNew={true} />
          </div>
        </div>
        <div className="split-page-pagination split-page-pagination-mobile-vertical-screen">
          <Pagination pageNum={Math.ceil(cameraItems.length / splitOption)} curPage={curPage} onPageChange={(page) => setCurPage(page)} itemNumPerPage={splitOption} total={cameraItems.length} isNew={true} />
        </div>
        <div className="split-page-detail-list-container">
          <div className="list_items_container" ref={containerRef}>
            <div className="list_camera_subcontainer" style={{ gridTemplateColumns: `repeat(${Math.round(Math.sqrt(splitOption))}, 1fr)`, width: calculateContainerWidth() > 0 ? `${calculateContainerWidth()}px` : "100%" }}>
              <CameraList key={`${selectedGroup}-${splitOption}-${curPage}`} curPage={curPage} items={cameraItems} itemNumPerPage={splitOption} />
            </div>
            {isFullscreen && (
              <button className="split-page-sensor-icon-button split-page-sensor-icon-button-exit" onClick={exitFullscreen}>
                <FontAwesomeIcon icon={faCompress} className="split-page-sensor-icon-button-icon" />
              </button>
            )}
          </div>
        </div>
      </div>
      {isLoading && <FanLoadingIcon />}
    </>
  );
};

export default SplitPage;
